import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Users, Table2, IdCard, Trophy, Target, Stethoscope, TrendingUp, UserCheck, Clock, PackageMinus } from "lucide-react";
import { IncentivoParado } from "../components/IncentivoParado";
import { ListaVender } from "../components/ListaVender";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { EmployeeTable, mediaFarmacia } from "./Ventas";
import Incentivos from "./Incentivos";
import ConsejoEmpleados from "./ConsejoEmpleados";
import { FichaEmpleado } from "../components/empleados/FichaEmpleado";
import { RankingEquipo } from "../components/empleados/RankingEquipo";
import { TendenciaAnual } from "../components/empleados/TendenciaAnual";
import { IncentivoGrupal } from "../components/empleados/IncentivoGrupal";
import { EstudioHorarios } from "../components/EstudioHorarios";
import { PersonalTab } from "../components/empleados/PersonalTab";
import { BannerFarmanaliza } from "../components/BannerFarmanaliza";

const TABS = [
  { id: "personal", label: "Personal", icon: UserCheck, mod: "comercial" },
  { id: "indicadores", label: "Indicadores del mes", icon: Table2, mod: "comercial", month: true },
  { id: "ficha", label: "Ficha del empleado", icon: IdCard, mod: "comercial", month: true },
  { id: "ranking", label: "Ranking del equipo", icon: Trophy, mod: "comercial", month: true },
  { id: "incentivos", label: "Incentivos", icon: Target, mod: "incentivos" },
  { id: "grupal", label: "Incentivo grupal", icon: TrendingUp, mod: "incentivos", month: true },
  { id: "parado", label: "Incentivo stock parado", icon: PackageMinus, mod: "incentivos" },
  { id: "horarios", label: "Horarios", icon: Clock, mod: "comercial", month: true },
  { id: "consejo", label: "Consejo y patologías", icon: Stethoscope, mod: "consejo" },
];

function useMonthData(tabId) {
  const [imports, setImports] = useState(null);
  const [importId, setImportId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    api.get("/sales/imports").then((r) => {
      setImports(r.data);
      const withTeam = r.data.find((i) => (i.employees || []).some((e) => e !== "SIN ASIGNAR"));
      setImportId((withTeam || r.data[0])?.id || null);
    }).catch(() => setImports([]));
    api.get("/sales/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/sales/employees/evolution").then((r) => setEvolution(r.data)).catch(() => {});

  }, []);

  useEffect(() => {
    if (!importId) return;
    setDetail(null);
    api.get(`/sales/imports/${importId}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [importId]);

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data.personal)).catch(() => {});
  }, [tabId]);

  return { imports, importId, setImportId, detail, history, evolution, staff };
}

function MonthTabs({ imports, importId, onChange }) {
  return (
    <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="empleados-month-selector">
      {imports.map((im) => (
        <button key={im.id} onClick={() => onChange(im.id)}
                className={"px-3 py-2 rounded-lg text-sm font-semibold border transition-colors " +
                  (importId === im.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400")}
                data-testid={`empleados-month-${im.id}`}>
          {im.label}
        </button>
      ))}
    </div>
  );
}

function VsTabla({ ref_, detail, rows, names, years }) {
  const testid = years ? "employees-vs-year-panel" : "employees-vs-prev-panel";
  if (!ref_) return <div className="card p-5 mb-6 text-sm text-slate-500" data-testid={`${testid}-vacio`}>{years ? "No hay ventas del mismo mes del año anterior subidas para comparar." : "No hay ventas del mes anterior subidas para comparar."}</div>;
  const pe = ref_.by_employee || [];
  const hist = {
    by_employee: Object.fromEntries(pe.map((e) => [e.vendedor, { ...e, n_meses: 2 }])),
    media_empleado: pe.length ? mediaFarmacia(pe) : null,
  };
  const corto = ref_.label.split(" (")[0].split(" · ")[0];
  return (
    <>
      {ref_.prorrateado && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2" data-testid={`${testid}-prorrateo`}>
          {corto} no cubre los mismos días: sus importes y operaciones se han prorrateado a los días de este periodo. Los porcentajes y medias no cambian.
        </div>
      )}
      <EmployeeTable names={names} rows={rows} hasTickets={detail.has_tickets} hist={hist} importId={detail.id}
                     title={`Indicadores por empleado frente a ${ref_.label}${years ? " — mismo mes del año anterior" : ""}`} refLabel={corto}
                     testid={testid} />
    </>
  );
}

function VsRefs({ detail, rows, names }) {
  const [refs, setRefs] = useState(null);
  useEffect(() => {
    setRefs(null);
    api.get(`/sales/imports/${detail.id}/employees-ref`).then((r) => setRefs(r.data)).catch(() => setRefs({}));
  }, [detail.id]);
  if (!refs) return <div className="text-slate-500 flex items-center gap-2 py-4"><Loader2 size={16} className="animate-spin" /> Cargando comparativas…</div>;
  return (
    <>
      <VsTabla ref_={refs.mom} detail={detail} rows={rows} names={names} />
      <VsTabla ref_={refs.yoy} detail={detail} rows={rows} names={names} years={1} />
    </>
  );
}

function MonthContent({ tab, m }) {
  const { detail, history, evolution } = m;
  if (tab === "grupal") return m.importId ? <IncentivoGrupal importId={m.importId} /> : null;
  if (tab === "horarios") return m.importId ? <EstudioHorarios importId={m.importId} /> : null;
  if (!detail) return <div className="text-slate-500 flex items-center gap-2 py-6"><Loader2 size={16} className="animate-spin" /> Cargando el mes…</div>;
  const fuera = new Set((m.staff || []).filter((s) => !s.activo).map((s) => s.vendedor));
  const rows = detail.by_employee || [];
  const activos = rows.filter((r) => !fuera.has(r.vendedor));
  if (!rows.length) return <div className="card p-8 text-center text-slate-500" data-testid="empleados-sin-equipo">Este fichero no trae ventas por empleado.</div>;
  const enough = (detail.totals?.tickets || 0) >= 20 && history?.n_meses > 1;
  if (tab === "indicadores") {
    const names = Object.fromEntries((m.staff || []).map((s) => [s.vendedor, s.nombre]));
    return (
      <>
        <EmployeeTable names={names} rows={rows} hasTickets={detail.has_tickets} hist={enough ? history : null} importId={detail.id} />
        <TendenciaAnual hasta={(detail.date_from || "").slice(0, 7)} names={names} />
        <VsRefs detail={detail} rows={rows} names={names} />
      </>
    );
  }
  if (tab === "ficha") return <FichaEmpleado key={detail.id} rows={activos.length ? activos : rows} staff={m.staff} hasTickets={detail.has_tickets} importId={detail.id} history={enough ? history : null} evolution={evolution} />;
  return <RankingEquipo names={Object.fromEntries((m.staff || []).map((s) => [s.vendedor, s.nombre]))} rows={activos} hasTickets={detail.has_tickets} />;
}

export default function Empleados() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const modules = user?.role === "admin" ? null : (user?.modules || null);
  const tabs = TABS.filter((t) => !modules || modules.includes(t.mod));
  const tab = tabs.find((t) => t.id === params.get("tab")) || tabs[0];
  const m = useMonthData(tab?.id);
  const tabsRef = useRef(null);
  useEffect(() => {
    const box = tabsRef.current;
    const el = box?.querySelector("[data-active]");
    if (box && el) box.scrollLeft = el.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2;
  }, [tab?.id]);

  return (
    <div className="p-8 fade-in" data-testid="empleados-page">
      {["incentivos", "grupal", "parado"].includes(tab?.id) ? (
        <BannerFarmanaliza compact image="/img/farmanaliza-incentivos.jpg?v=2" kicker="Incentivos" subtitle="Un equipo motivado vende más"
                           text="Incentivos individuales, de grupo y de stock D para premiar a tu equipo." testid="banner-incentivos" />
      ) : (
        <BannerFarmanaliza compact image="/img/farmanaliza-equipo.jpg?v=2" kicker="Equipo" subtitle="Tu equipo, sus resultados y sus incentivos"
                           text="Indicadores, fichas individuales, ranking, incentivos y consejo de cada empleado." testid="banner-equipo" />
      )}
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Equipo</div>
      <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight flex items-center gap-3"><Users size={30} /> Empleados</h1>
      <p className="text-slate-600 mt-1 text-sm mb-6">Todo lo de tu equipo en un sitio: indicadores, fichas individuales, ranking, incentivos y consejo.</p>

      <div ref={tabsRef} className="relative flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto" data-testid="empleados-tabs">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setParams({ tab: t.id })}
                  data-active={tab?.id === t.id ? "1" : undefined}
                  className={"px-4 py-2.5 text-sm font-semibold whitespace-nowrap inline-flex items-center gap-2 border-b-2 -mb-px transition-colors " +
                    (tab?.id === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800")}
                  data-testid={`empleados-tab-${t.id}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab?.month && (
        m.imports == null ? <div className="text-slate-500 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Cargando…</div>
        : !m.imports.length ? <div className="card p-10 text-center text-slate-500" data-testid="empleados-empty">Sube las ventas del mes para ver los datos de tu equipo.</div>
        : <>
            <MonthTabs imports={m.imports} importId={m.importId} onChange={m.setImportId} />
            <MonthContent tab={tab.id} m={m} />
          </>
      )}
      {tab?.id === "personal" && <PersonalTab />}
      {tab?.id === "incentivos" && <Incentivos embedded />}
      {tab?.id === "parado" && <div className="grid grid-cols-1 gap-6" data-testid="tab-incentivo-parado"><IncentivoParado /><ListaVender /></div>}
      {tab?.id === "consejo" && <ConsejoEmpleados embedded />}
    </div>
  );
}
